import React from 'react';
import { Link } from 'react-router-dom';
import { Linkedin, Twitter, Youtube, Mail, MapPin, ArrowUpRight } from 'lucide-react';
import { Container } from '../ui';
import { COMPANY_NAME, CONTACT_INFO, SOCIAL_LINKS } from '../../constants';
import FullLogo from '../../assets/images/Full_logo.png';
// D&B / DUNS-registered profile page for Istatec, saved as a static
// PNG in `src/assets/`. Clicking the seal in the footer opens this
// image in a new tab so visitors see our own registered profile
// certificate rather than being routed to D&B's dynamic verification
// widget. Imported (rather than referenced from `/public`) so Vite
// fingerprints the URL and gets long-lived caching for free.
import DunsCertificateImage from '../../assets/D&B_Registration_Certificate.png';

/*
 * Footer link groups
 * ------------------
 * All internal `href`s are real router paths. Service slugs are the
 * `id` fields from `src/data/whatWeDo.ts` so `/services/<slug>` always
 * resolves to a working `ServiceDetail` page — if you rename a slug
 * there, also update it here. `external: true` marks a link that
 * should render as an `<a target="_blank">` instead of a `<Link>`.
 */
const footerLinks = {
  services: [
    { label: 'AI / GenAI Automations', href: '/services/ai-genai-automations' },
    { label: 'Artificial Intelligence', href: '/services/artificial-intelligence' },
    { label: 'Cloud, Infra & Security', href: '/services/cloud-infrastructure-security' },
    { label: 'Data Engineering & Analytics', href: '/services/data-engineering-analytics' },
    { label: 'IT Consultancy & Augmentation', href: '/services/it-consultancy-augmentation' },
    { label: 'Cybersecurity Automation', href: '/services/cybersecurity-automation' },
  ],
  company: [
    { label: 'About Us', href: '/about' },
    { label: 'Careers', href: '/careers' },
    { label: 'Investors', href: '/investors' },
    { label: 'IstaLabs', href: '/istalabs' },
    { label: 'Services', href: '/services' },
    { label: 'Contact', href: '/contact' },
  ],
  // "Support" (was "Resources"). The FinExtract API + API Documentation
  // links used to live here — they've been removed at the client's
  // request. Only human-support entry points remain.
  support: [
    { label: 'Get Support', href: '/contact?inquiry=support' },
    { label: 'Schedule a Call', href: '/contact?inquiry=consultation' },
  ],
};

export const Footer: React.FC = () => {
  // The company was founded in 2023, so the copyright reads "2023–<this
  // year>" — dynamic on the trailing side so the range keeps growing
  // automatically every January without us having to touch the code.
  const FOUNDED_YEAR = 2023;
  const currentYear = new Date().getFullYear();
  const copyrightYears =
    currentYear > FOUNDED_YEAR ? `${FOUNDED_YEAR}–${currentYear}` : `${FOUNDED_YEAR}`;

  return (
    <footer className="bg-black text-white">
      {/* Main Footer */}
      <div className="py-16 border-b border-white/10">
        <Container>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 lg:gap-8">
            {/* Brand Column */}
            <div className="lg:col-span-2">
              <Link
                to="/"
                aria-label={`${COMPANY_NAME} home`}
                className="inline-flex items-center mb-6 group"
              >
                {/* Full logo (istatec wordmark + tagline). The header uses the
                    same asset at h-9 → h-12; here we render it noticeably
                    bigger for the footer's brand section. */}
                <img
                  src={FullLogo}
                  alt={COMPANY_NAME}
                  className="h-20 md:h-24 w-auto object-contain transition-transform group-hover:scale-[1.02]"
                />
              </Link>
              <p className="text-navy-300 mb-6 max-w-sm leading-relaxed">
                Empowering businesses with cutting-edge technology consulting, 
                software product development, and expert technical talent.
              </p>
              
              {/* Contact Info */}
              <div className="space-y-3 text-navy-300">
                <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-3 hover:text-white transition-colors">
                  <Mail className="w-5 h-5 text-accent-400" />
                  {CONTACT_INFO.email}
                </a>
                <div className="flex items-start gap-3">
                  <MapPin className="w-5 h-5 text-accent-400 flex-shrink-0 mt-0.5" />
                  <span>{CONTACT_INFO.address}</span>
                </div>
              </div>

              {/* ─────────────────── D&B / DUNS trust seal ───────────────────
                  Compact "Globally registered" badge served by Dun &
                  Bradstreet from `dunsregistered.dnb.com`. Rendered as
                  an <iframe> per D&B's official React/Next.js snippet.

                  Sizing note: the seal iframe has a fixed 114×97 payload;
                  D&B doesn't offer a smaller variant. To render at half
                  size we can't just shrink the <iframe> element (its
                  contents would clip), so we wrap it in an outer box
                  that reserves half-sized layout (`57×49`) and
                  CSS-transform-scale the iframe by 0.5 from its
                  top-left corner. Result: the seal visually occupies
                  57×49 while staying pixel-crisp.

                  Click behaviour: clicking the seal should open our
                  own **D-U-N-S Registered profile certificate** image
                  in a new tab — NOT D&B's dynamic verification page
                  (which is what the iframe would open on its own).

                  How that's wired:
                   1. The whole seal is wrapped in an <a target="_blank">
                      pointing at `DunsCertificateImage` (the imported
                      PNG). That's the actual click target.
                   2. The inner iframe wrapper has `pointer-events-none`
                      so clicks (and the D&B iframe's own JS handlers)
                      fall right through to the <a>. No matter where
                      inside the seal box the user clicks, they land on
                      our certificate image, never on D&B's page.
                   3. `tabIndex={-1}` on the iframe keeps keyboard tab
                      order flowing to the outer anchor, not into the
                      D&B document. */}
              <div className="mt-8 pt-6 border-t border-white/10">
                <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-navy-400 mb-2">
                  Our Global Registration
                </p>
                <p className="text-xs text-navy-400 mb-3">
                  We&rsquo;re globally registered with Dun &amp; Bradstreet.
                </p>
                <a
                  href={DunsCertificateImage}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="View Istatec's D-U-N-S Registered profile certificate (opens in a new tab)"
                  title="View our D-U-N-S Registered profile"
                  className="inline-block rounded-sm ring-1 ring-white/10 hover:ring-accent-400/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-400 transition-shadow"
                >
                  <div
                    className="block overflow-hidden pointer-events-none select-none"
                    style={{ width: 57, height: 49 }}
                    aria-hidden
                  >
                    <iframe
                      id="Iframe1"
                      title="D-U-N-S Registered — Dun & Bradstreet global registration seal"
                      src="https://dunsregistered.dnb.com/SealAuthentication.aspx?Cid=1"
                      width={114}
                      height={97}
                      frameBorder={0}
                      scrolling="no"
                      allowTransparency
                      tabIndex={-1}
                      style={{
                        transform: 'scale(0.5)',
                        transformOrigin: 'top left',
                        border: 0,
                        display: 'block',
                      }}
                    />
                  </div>
                </a>
              </div>
            </div>

            {/* Services */}
            <div>
              <h4 className="text-lg font-semibold mb-5">Services</h4>
              <ul className="space-y-3">
                {footerLinks.services.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-navy-300 hover:text-white transition-colors inline-flex items-center gap-1 group"
                    >
                      {link.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-lg font-semibold mb-5">Company</h4>
              <ul className="space-y-3">
                {footerLinks.company.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-navy-300 hover:text-white transition-colors inline-flex items-center gap-1 group"
                    >
                      {link.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Support (was "Resources" — FinExtract API + API
                Documentation entries were removed; only human-support
                touchpoints remain, so the column was renamed to match
                what's actually inside it.) */}
            <div>
              <h4 className="text-lg font-semibold mb-5">Support</h4>
              <ul className="space-y-3">
                {footerLinks.support.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.href}
                      className="text-navy-300 hover:text-white transition-colors inline-flex items-center gap-1 group"
                    >
                      {link.label}
                      <ArrowUpRight className="w-3 h-3 opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </div>

      {/* Bottom Footer */}
      <div className="py-6">
        <Container>
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-navy-400 text-sm">
              © {copyrightYears} {COMPANY_NAME}. All rights reserved.
            </p>
            
            {/* Social Links */}
            <div className="flex items-center gap-4">
              <a
                href={SOCIAL_LINKS.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-navy-800 text-navy-300 hover:bg-accent-500 hover:text-white transition-all"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
              <a
                href={SOCIAL_LINKS.twitter}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-navy-800 text-navy-300 hover:bg-accent-500 hover:text-white transition-all"
                aria-label="X (Twitter)"
              >
                <Twitter className="w-5 h-5" />
              </a>
              <a
                href={SOCIAL_LINKS.youtube}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 flex items-center justify-center rounded-full bg-navy-800 text-navy-300 hover:bg-accent-500 hover:text-white transition-all"
                aria-label="YouTube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a
                href={`mailto:${CONTACT_INFO.email}`}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-navy-800 text-navy-300 hover:bg-accent-500 hover:text-white transition-all"
                aria-label="Email"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>

            {/* Legal Links */}
            <div className="flex items-center gap-6 text-sm text-navy-400">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
            </div>
          </div>
        </Container>
      </div>
    </footer>
  );
};
